import { debounce } from 'lodash';

import { PaymentDetails } from '@/components/patient/checkout/checkout-actions';
import { StripeCharge } from '@/db/queries/stripe-charges';

import { LineItems, lineItemsSchema } from './stripe-charge';
import { wait } from './wait';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

export type GtagEvent =
  | 'start_quiz'
  // One per questionnaire wizard step, fired when the step is completed. Between start_quiz and
  // submit_quiz these show which step people abandon on.
  | 'quiz_step_pets'
  | 'quiz_step_questions'
  | 'quiz_step_details'
  | 'submit_quiz'
  | 'add_pet'
  | 'save_pet'
  | 'sign_in'
  // A typo the hint caught is a typo that never reaches the database, so it leaves no other
  // trace. Without these two the only signal is the absence of new typos, which takes months
  // to read at a 0.6% base rate.
  | 'email_typo_suggested'
  | 'email_typo_accepted';

export async function gtagEvent(event: GtagEvent) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event });

  console.info('🏷️ gtagEvent', event);

  return wait(500);
}

export const gtagBeginCheckout = debounce(function gtagBeginCheckout(paymentDetails: PaymentDetails) {
  const paymentInfo = {
    event: 'begin_checkout',
    ecommerce: {
      ...parsePaymentDetails(paymentDetails),
    },
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(paymentInfo);

  console.info('🏷️ gtagBeginCheckout', paymentInfo);
}, 100);

export function gtagAddPaymentInfo(paymentDetails: PaymentDetails) {
  const paymentInfo = {
    event: 'add_payment_info',
    ecommerce: {
      ...parsePaymentDetails(paymentDetails),
      coupon: paymentDetails.discountCode,
    },
  };
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(paymentInfo);

  console.info('🏷️ gtagAddPaymentInfo', paymentInfo);
}

export const gtagCharge = debounce(function gtagCharge(charge: StripeCharge) {
  const paymentInfo = {
    event: 'purchase',
    ecommerce: {
      transaction_id: charge.id,
      currency: 'USD',
      value: charge.amount / 100,
      coupon: charge.code,
      items: parseLineItems(lineItemsSchema.parse(charge.lineItems)),
    },
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(paymentInfo);

  console.info('🏷️ gtagCharge', paymentInfo);
}, 100);

function parsePaymentDetails(paymentDetails: PaymentDetails) {
  return {
    currency: 'USD',
    value: paymentDetails.amountCents / 100,
    items: parseLineItems(lineItemsSchema.parse(paymentDetails.lineItems)),
  };
}

export function parseLineItems(lineItems: LineItems) {
  const positiveLineItems = lineItems.filter((lineItem) => lineItem.cents > 0);
  const negativeLineItems = lineItems.filter((lineItem) => lineItem.cents < 0);

  const result = positiveLineItems.map((lineItem) => ({
    item_id: lineItem.id,
    item_name: lineItem.description,
    discount: 0,
    price: lineItem.cents / 100,
    quantity: 1,
  }));

  for (const discountItem of negativeLineItems) {
    const centsOffPerItem = -discountItem.cents / positiveLineItems.length;

    for (const item of result) {
      const discount = centsOffPerItem / 100;

      item.discount += discount;
      item.price -= discount;
    }
  }

  return result;
}
